<template>
  <router-link :to="`/car/${encodeURIComponent(car.brand)}/${encodeURIComponent(car.model)}/${car.year}/${car.id}`" class="car-item">
    <div class="car-item--img">
      <img :src="car.image || `/frontend/assets/images/placeholder.jpg`" :alt="car.brand + ' ' + car.model" />
      <div class="badges">
        <span
          class="badge"
          :class="{
            'badge--available': car.status === 'Available',
            'badge--sold': car.status === 'Sold',
            'badge--sale': car.status === 'Sale',
          }"
        >
          {{ car.status }}
        </span>
      </div>
    </div>

    <div class="car-item--info">

      <!-- 1. Header: Brand / Model / Trim -->
      <div class="car-item--header notranslate" translate="no">
        <div class="car-item--brand">{{ car.brand }}</div>
        <div class="car-item--model">{{ car.model }}</div>
        <div v-if="car.trimLine" class="car-item--trim">
          <span v-for="(feature, index) in trimFeatures" :key="index">
            <span class="trim-sep" v-if="index > 0"> · </span>{{ feature }}
          </span>
        </div>
      </div>

      <!-- 2. Price — hero element -->
      <div class="car-item--prices notranslate" translate="no" v-if="car.status !== 'Sold' && car.status !== 'sold'">
        <div class="price--netto" v-if="car.nettoPrice > 0">
          {{ formatPrice(car.nettoPrice) }} <span class="price--currency">€</span>
          <span class="price--netto-label">NETTO</span>
        </div>
        <div class="price--gross" v-if="car.grossPrice > 0">
          {{ formatPrice(car.grossPrice) }} € <span class="price--vat-label">VAT incl.</span>
        </div>
      </div>

      <!-- 3. B2B pricing: the card is a link, so the button has to stop it -->
      <button
        v-if="car.status !== 'Sold' && car.status !== 'sold'"
        class="car-item--b2b"
        type="button"
        @click.prevent.stop="openB2B"
      >
        Get B2B prices
      </button>

      <!-- 4. Bottom: specs + STOCK -->
      <div class="car-item--footer">
        <div class="car-item--specs" v-if="specTags.length > 0">
          <span v-for="(spec, index) in specTags" :key="index">
            <span class="spec-sep" v-if="index > 0"> · </span>{{ spec }}
          </span>
        </div>
        <span class="car-item--details">Details →</span>
      </div>

    </div>
  </router-link>
</template>

<script>
import { b2bModal } from '../b2bModal.js';
export default {
  props: {
    car: {
      type: Object,
      required: true,
    },
  },
  computed: {
    trimFeatures() {
      if (!this.car.trimLine) return [];
      return this.car.trimLine.split(',').map(f => f.trim()).filter(Boolean);
    },
    specTags() {
      const tags = [];
      if (this.car.year) tags.push(String(this.car.year));
      if (this.car.mileage) tags.push(Number(this.car.mileage).toLocaleString('sk-SK') + ' km');
      return tags;
    },
  },
  methods: {
    openB2B() {
      b2bModal.show(this.car, 'card');
    },

    formatPrice(price) {
      if (!price || price === 0) return 'POA';
      const num = typeof price === 'string' ? parseFloat(price) : price;
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(num);
    },
  },
};
</script>

<style scoped>
/* ── Card shell ── */
.car-item {
  display: flex;
  flex-direction: row;
  text-decoration: none;
  background-color: #0e0e0e;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
  transition: border-color 0.3s ease, background-color 0.3s ease;
  margin-bottom: 1rem;
  max-height: 260px;
}

.car-item:hover {
  background-color: #131313;
  border-color: rgba(255, 255, 255, 0.14);
}

/* ── Image ── */
.car-item--img {
  position: relative;
  flex: 0 0 44%;
  max-width: 44%;
  overflow: hidden;
}

.car-item--img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  aspect-ratio: 4/3;
  transition: transform 0.5s ease;
}

.car-item:hover .car-item--img img {
  transform: scale(1.04);
}

/* ── Badges (image overlay) ── */
.badges {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 0;
}

.badge--available { background-color: #fff; color: #000; }
.badge--sold      { background-color: #2a2a2a; color: rgba(255,255,255,0.5); }
.badge--sale      { background-color: #c9a84c; color: #000; }

/* ── Info panel ── */
.car-item--info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem 1.75rem;
  gap: 0.75rem;
}

/* ── 1. Header ── */
.car-item--brand {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 0.2rem;
}

.car-item--model {
  font-size: 1.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #ffffff;
  line-height: 1.15;
}

.car-item--trim {
  margin-top: 0.45rem;
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  letter-spacing: 0.02em;
}

.trim-sep {
  color: rgba(255, 255, 255, 0.2);
  margin: 0 0.1rem;
}

/* ── 2. Prices ── */
.car-item--b2b {
  display: block;
  width: 100%;
  margin: 1.25rem 0 0;
  padding: 0.7rem 1rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 0;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.car-item--b2b:hover {
  background: #ffffff;
  border-color: #ffffff;
  color: #000000;
}

.car-item--prices {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.price--netto {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.02em;
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
}

.price--currency {
  font-size: 1.1rem;
  font-weight: 400;
}

.price--netto-label {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.3);
  margin-left: 0.2rem;
}

.price--gross {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 0.01em;
}

.price--vat-label {
  font-size: 0.65rem;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.25);
  margin-left: 0.15rem;
}

/* ── 3. Footer: specs + STOCK ── */
.car-item--footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 0.85rem;
  gap: 1rem;
}

.car-item--specs {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.02em;
}

.car-item--details {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #ffffff;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s;
}
.car-item:hover .car-item--details { opacity: 1; }

.spec-sep {
  color: rgba(255, 255, 255, 0.15);
  margin: 0 0.15rem;
}

.car-item--stock {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #ffffff;
  white-space: nowrap;
  flex-shrink: 0;
}

.stock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #ffffff;
  display: inline-block;
  flex-shrink: 0;
}

/* ── Mobile ── */
@media (max-width: 767px) {
  .car-item { max-height: none; }

  .car-item--img {
    flex: 0 0 36%;
    max-width: 36%;
  }

  .car-item--img img {
    aspect-ratio: 3 / 4;
    height: 100%;
    min-height: 140px;
  }

  .car-item--info {
    padding: 0.75rem 0.9rem;
    gap: 0.35rem;
  }

  .car-item--brand { font-size: 0.52rem; margin-bottom: 0.1rem; }
  .car-item--model { font-size: 1rem; line-height: 1.2; }
  .car-item--trim  { margin-top: 0.2rem; font-size: 0.72rem; line-height: 1.4; }

  .price--netto   { font-size: 1.25rem; gap: 0.2rem; }
  .price--currency { font-size: 1rem; }
  .price--gross   { font-size: 0.72rem; }

  .car-item--footer { padding-top: 0.5rem; gap: 0.4rem; }
  .car-item--specs  { font-size: 0.7rem; line-height: 1.4; }
  .car-item--stock  { font-size: 0.55rem; }
}
</style>
